/**
 * Valor com a origem e a ressalva coladas nele.
 *
 * 🛑 **É o componente mais importante da tela de recomendação.** Nem tudo o que ela
 * apresenta foi medido: o desempenho previsto para um candidato deriva da eficiência
 * observada em outro hardware, e essa derivação carrega limitações concretas.
 *
 * A ressalva viaja **junto do número**, e não em nota de rodapé nem escondida em dica de
 * ponteiro. Nota de rodapé não é lida — é o mecanismo pelo qual uma limitação
 * corretamente escrita deixa de ter efeito, e a decisão acaba tomada como se o valor
 * fosse medido.
 */

import { Badge } from "@cloudflare/kumo";
import type { ProvenancedValue } from "../fixtures/index.js";
import { formatNumber } from "../lib/format.js";

/** Aparência de cada origem: medido é neutro, extrapolado precisa saltar. */
const PROVENANCE_STYLE = {
  measured: { variant: "info", label: "medido" },
  extrapolated: { variant: "warning", label: "extrapolado" },
} as const;

/** Entradas do valor: o dado com origem, e como formatá-lo. */
interface Props {
  readonly value: ProvenancedValue;
  readonly suffix?: string;
  readonly precision?: number;
  /** Corpo grande para número de destaque; normal dentro de tabela. */
  readonly size?: "large" | "normal";
}

/** Exibe um número com o selo de origem e, se extrapolado, a limitação logo abaixo. */
export function ProvenanceValue({
  value,
  suffix = "",
  precision = 0,
  size = "normal",
}: Props): React.ReactElement {
  const style = PROVENANCE_STYLE[value.provenance];

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className={`font-semibold text-kumo-strong ${size === "large" ? "text-2xl" : "text-sm"}`}>
          {formatNumber(value.value, precision)}
          {suffix !== "" && <span className="ml-1 text-xs font-normal text-kumo-subtle">{suffix}</span>}
        </span>
        <Badge variant={style.variant}>{style.label}</Badge>
      </div>

      {/* A limitação fica AQUI, colada no valor — nunca em rodapé. */}
      {value.limitation !== null && (
        <p className="max-w-xs text-xs leading-snug text-kumo-warning">⚠ {value.limitation}</p>
      )}
    </div>
  );
}
